import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AktifDers {
  id: string;
  ders_tarihi: string;
  brans: string | null;
  ders_suresi: number | null;
  odeme_durumu: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface OdemeRow {
  id: string;
  dekont_tarihi: string;
  tutar: number;
  ogretmen_payi: number | null;
  yonetici_onay: boolean | null;
  ogrenci?: { ogrenci_adi: string };
}

interface HakEdisOdeme {
  odeme_tutari: number;
  odeme_tarihi: string;
}

interface MonthData { month: string; kazanç: number; }

export default function KazanclarimPage() {
  const { ogretmenRecord } = useAuth();
  const [lessons, setLessons] = useState<AktifDers[]>([]);
  const [payments, setPayments] = useState<OdemeRow[]>([]);
  const [hakOdemeleri, setHakOdemeleri] = useState<HakEdisOdeme[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    const tid = ogretmenRecord.id;
    const fetchAll = async () => {
      const [{ data: l }, { data: p }, { data: h }] = await Promise.all([
        supabase.from('egtpanel_aktif_dersler').select('id, ders_tarihi, brans, ders_suresi, odeme_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', tid).order('ders_tarihi', { ascending: false }),
        supabase.from('egtpanel_odemeler').select('id, dekont_tarihi, tutar, ogretmen_payi, yonetici_onay, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', tid).order('dekont_tarihi', { ascending: false }),
        supabase.from('egtpanel_hak_edis_odemeleri').select('odeme_tutari, odeme_tarihi').eq('ogretmen_id', tid).order('odeme_tarihi', { ascending: false }),
      ]);
      setLessons((l || []) as AktifDers[]);
      setPayments((p || []) as OdemeRow[]);
      setHakOdemeleri(h || []);

      // Build monthly chart
      const approvedPayments = (p || []).filter(x => x.yonetici_onay);
      const byMonth: Record<string, number> = {};
      approvedPayments.forEach(x => {
        const month = x.dekont_tarihi?.slice(0, 7) || '';
        if (!month) return;
        byMonth[month] = (byMonth[month] || 0) + Number(x.ogretmen_payi || 0);
      });
      const months = Object.keys(byMonth).sort().slice(-6);
      const monthNames: Record<string, string> = { '01': 'Oca', '02': 'Şub', '03': 'Mar', '04': 'Nis', '05': 'May', '06': 'Haz', '07': 'Tem', '08': 'Ağu', '09': 'Eyl', '10': 'Eki', '11': 'Kas', '12': 'Ara' };
      setMonthlyData(months.map(m => ({ month: monthNames[m.slice(5)] || m, kazanç: byMonth[m] })));
      setLoading(false);
    };
    fetchAll();
  }, [ogretmenRecord]);

  const oran = ogretmenRecord?.hakedis_orani || 50;
  const approvedPayments = payments.filter(p => p.yonetici_onay);
  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 7) + '-01';
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

  const thisMonthEarning = approvedPayments.filter(p => p.dekont_tarihi >= monthStart).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);
  const thisWeekEarning = approvedPayments.filter(p => p.dekont_tarihi >= weekAgo).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);
  const totalEarning = approvedPayments.reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);
  const totalPaid = hakOdemeleri.reduce((a, h) => a + Number(h.odeme_tutari), 0);
  const pendingEarning = Math.max(0, totalEarning - totalPaid);
  const lastPayment = hakOdemeleri[0]?.odeme_tarihi || '—';

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Kazançlarım</h1>
        <p className="text-slate-500 text-sm mt-1">Hak ediş ve ödeme durumunuz (Hak Ediş Oranı: %{oran})</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Bu Hafta Kazanç', val: `₺${thisWeekEarning.toLocaleString('tr-TR')}`, color: 'text-slate-800', bg: 'bg-slate-50', icon: 'ri-calendar-2-line' },
          { label: 'Bu Ay Kazanç', val: `₺${thisMonthEarning.toLocaleString('tr-TR')}`, color: 'text-purple-700', bg: 'bg-purple-50', icon: 'ri-calendar-line' },
          { label: 'Toplam Kazanç', val: `₺${totalEarning.toLocaleString('tr-TR')}`, color: 'text-[#1e3a5f]', bg: 'bg-[#1e3a5f]/5', icon: 'ri-money-dollar-circle-line' },
          { label: 'Ödenen Tutar', val: `₺${totalPaid.toLocaleString('tr-TR')}`, color: 'text-green-700', bg: 'bg-green-50', icon: 'ri-checkbox-circle-line' },
          { label: 'Bekleyen Tutar', val: `₺${pendingEarning.toLocaleString('tr-TR')}`, color: 'text-amber-700', bg: 'bg-amber-50', icon: 'ri-time-line' },
          { label: 'Son Ödeme', val: lastPayment, color: 'text-slate-700', bg: 'bg-slate-50', icon: 'ri-calendar-check-line' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl shrink-0">
              <i className={`${s.icon} ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-base font-bold ${s.color}`}>{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      {monthlyData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Aylık Kazanç Grafiği</h2>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `₺${v.toLocaleString('tr-TR')}`} />
              <Tooltip formatter={(v: number) => [`₺${v.toLocaleString('tr-TR')}`, 'Kazanç']} />
              <Area type="monotone" dataKey="kazanç" stroke="#7c3aed" fill="#7c3aed15" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Ders Bazlı Kayıtlar</h2>
        {lessons.length === 0 ? (
          <div className="text-center py-8"><i className="ri-book-open-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Ders kaydı bulunamadı</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-xs text-slate-500">
                <th className="text-left pb-2 pr-4">Tarih</th>
                <th className="text-left pb-2 pr-4">Öğrenci</th>
                <th className="text-left pb-2 pr-4">Branş</th>
                <th className="text-left pb-2 pr-4">Süre</th>
                <th className="text-left pb-2">Ödeme</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {lessons.map(l => {
                  const ogr = l.ogrenci as {ogrenci_adi:string}|undefined;
                  return (
                    <tr key={l.id}>
                      <td className="py-2.5 pr-4 text-slate-600">{l.ders_tarihi}</td>
                      <td className="py-2.5 pr-4 font-medium text-slate-800">{ogr?.ogrenci_adi || '—'}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{l.brans || '—'}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{l.ders_suresi ? `${l.ders_suresi} dk` : '—'}</td>
                      <td className="py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.odeme_durumu === 'Ödendi' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{l.odeme_durumu || 'Bekliyor'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Dekont Bazlı Kazanç</h2>
        {approvedPayments.length === 0 ? (
          <div className="text-center py-8"><i className="ri-money-dollar-circle-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Onaylı ödeme bulunamadı</p></div>
        ) : (
          <div className="space-y-3">
            {approvedPayments.map(p => {
              const ogr = p.ogrenci as {ogrenci_adi:string}|undefined;
              return (
                <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-slate-50">
                  <div>
                    <span className="text-sm font-medium text-slate-800">{ogr?.ogrenci_adi || '—'}</span>
                    <span className="text-xs text-slate-400 ml-2">{p.dekont_tarihi}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Toplam: ₺{Number(p.tutar).toLocaleString('tr-TR')}</div>
                    <div className="text-sm font-semibold text-purple-700">Payım: ₺{Number(p.ogretmen_payi || 0).toLocaleString('tr-TR')}</div>
                  </div>
                </div>
              );
            })}
            <div className="bg-purple-50 rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-purple-800">Toplam Onaylı Kazanç</span>
              <span className="text-lg font-bold text-purple-700">₺{approvedPayments.reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0).toLocaleString('tr-TR')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}